import { LegalLayout } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout otherLink={{ to: "/terms", label: "Terms & Conditions" }}>
    <div className="py-32" data-testid="privacy-page">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="bg-white p-8 md:p-12 shadow-lg">
          <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase mb-8">
            Privacy Policy
          </h1>
          
          <p className="text-sm text-gray-600 mb-8">Last Updated: October 23, 2026</p>
          
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              This privacy notice for Orlando Rivera for New York State Senate ("committee," "we," "us," or "our") explains how and why we may collect, store, use, and share information when you interact with our campaign, website, forms, communications, events, donation pages, volunteer opportunities, SMS/MMS text messaging program, email communications, or other campaign-related activities.
            </p>
            
            <p className="font-semibold">This Privacy Policy applies when you:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Visit our website or any website of ours that links to this Privacy Policy</li>
              <li>Register for events</li>
              <li>Sign up to receive campaign updates</li>
              <li>Volunteer or request more information</li>
              <li>Make a donation</li>
              <li>Engage with us through SMS/MMS text messages, email, forms, marketing, events, or other campaign communications</li>
            </ul>
            
            <p>
              Reading this Privacy Policy will help you understand how Orlando Rivera for New York State Senate collects, uses, and protects information. If you do not agree with this Privacy Policy, please do not use our website, forms, communications, or services.
            </p>
            
            <p>
              If you have questions or concerns, please contact us at <a href="mailto:info@orlandojrivera.com" data-testid="privacy-email-link-1" className="text-campaignRed hover:underline">info@orlandojrivera.com</a>.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Summary of Key Points
            </h2>
            <p>This summary provides key points from our Privacy Policy.</p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              What Personal Information Do We Collect?
            </h2>
            <p>
              When you visit, use, or interact with our website, forms, events, donation pages, volunteer opportunities, SMS/MMS text messaging program, email communications, or other campaign services, we may collect personal information depending on how you interact with Orlando Rivera for New York State Senate.
            </p>
            
            <h3 className="font-oswald text-xl font-bold uppercase mt-6 mb-3 text-campaignBlack">
              Personal Information You Provide
            </h3>
            <p>The personal information we collect may include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Phone number</li>
              <li>Mobile phone number</li>
              <li>Email address</li>
              <li>Mailing address</li>
              <li>Event registration information</li>
              <li>Volunteer or supporter information</li>
              <li>Donation-related information</li>
              <li>Messages or responses you send to us</li>
            </ul>
            
            <h3 className="font-oswald text-xl font-bold uppercase mt-6 mb-3 text-campaignBlack">
              Technical, Device, and Security Information
            </h3>
            <p>
              When you access our website, we and the service providers that operate and protect it may automatically collect limited technical information. This information may include your Internet Protocol (IP) address; request date and time; pages, files, and links requested; referring website or domain; browser type and version; operating system; device type; user-agent string; preferred language; time zone; screen or viewport dimensions when available; approximate geographic region derived from an IP address; network or autonomous system number (ASN); connection protocol; response status; security-event information; bot or automated-traffic indicators; and similar diagnostic metadata.
            </p>
            
            <p>
              We collect and use this technical information for website and network security, fraud and abuse prevention, incident investigation, troubleshooting, performance monitoring, and ensuring that website content and features operate correctly across browsers, operating systems, screen sizes, networks, and devices. This technical information is not collected for the purpose of determining a visitor's real-world identity solely from ordinary browsing activity.
            </p>
            
            <p>
              We may use third-party technical service providers for IP-related lookup, security, and compatibility functions. These providers may process a visitor's IP address, requested page URL, and related request metadata as necessary to provide their services. We use this information for the security, troubleshooting, diagnostic, and compatibility purposes described above.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              SMS/MMS Text Messaging
            </h2>
            <p>
              If you provide your mobile phone number and opt in to receive text messages, you agree to receive recurring SMS/MMS text messages from Orlando Rivera for New York State Senate. Messages may include campaign alerts, donation requests, updates, voter outreach, event invitations, polling questions, get-out-the-vote reminders, election information, and other important campaign information.
            </p>
            
            <p className="font-semibold">
              Message frequency varies. Msg & data rates may apply. Reply HELP for help. Reply STOP to opt out at any time.
            </p>
            
            <p>
              Consent to receive SMS/MMS text messages is not a condition of making a donation, volunteering, supporting the campaign, or using this website.
            </p>
            
            <p>
              Orlando Rivera for New York State Senate does not sell, rent, or share mobile opt-in data or SMS consent with third parties for their own marketing purposes. Mobile phone numbers and SMS consent information may be shared with service providers only as necessary to operate campaign communications, comply with law, or support campaign operations.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              How We Use Your Information
            </h2>
            <p>Orlando Rivera for New York State Senate may use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Communicate with voters, supporters, volunteers, donors, and community members</li>
              <li>Send campaign updates, voter outreach, event invitations, volunteer opportunities, fundraising reminders, polling questions, get-out-the-vote reminders, election information, and public service announcements</li>
              <li>Respond to questions, requests, replies, or other communications</li>
              <li>Process event registrations, volunteer sign-ups, supporter forms, and donations</li>
              <li>Improve our website, forms, communications, and campaign operations</li>
              <li>Maintain website security and investigate malicious, fraudulent, abusive, or automated activity</li>
              <li>Diagnose errors and ensure compatibility across browsers, operating systems, screen sizes, networks, and devices</li>
              <li>Comply with legal, reporting, regulatory, security, and campaign-related obligations</li>
              <li>Protect against misuse, fraud, unauthorized activity, or security incidents</li>
            </ul>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              How We Share Information
            </h2>
            <p className="font-semibold">
              Orlando Rivera for New York State Senate does not sell personal information.
            </p>
            
            <p>
              We may share information with service providers, vendors, consultants, or other partners who help us operate the campaign, website, forms, SMS/MMS messaging, email communications, donation processing, event registration, data management, compliance, security, or other campaign operations.
            </p>
            
            <p>
              These service providers may only use information as needed to provide services to Orlando Rivera for New York State Senate, comply with law, or support campaign operations.
            </p>
            
            <p>
              Mobile opt-in data and SMS consent are not sold, rented, or shared with third parties for their own marketing purposes.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Do We Process Sensitive Personal Information?
            </h2>
            <p>
              We may process sensitive personal information only when necessary with your consent or as otherwise permitted by applicable law.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Do We Receive Information From Third Parties?
            </h2>
            <p>
              We may receive information from public databases, voter files, public records, marketing partners, social media platforms, service providers, and other lawful sources.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              How Do We Keep Your Information Safe?
            </h2>
            <p>
              We use reasonable organizational, technical, and administrative measures designed to protect personal information. However, no electronic transmission over the internet or information storage system can be guaranteed to be 100% secure. We cannot promise or guarantee that unauthorized third parties will never be able to defeat our security measures or improperly collect, access, steal, or modify information.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Technical Log Retention
            </h2>
            <p>
              Technical and security logs may be retained for as long as reasonably necessary to protect the website, investigate security incidents, diagnose compatibility or performance problems, maintain reliable operations, comply with legal obligations, and resolve disputes.
            </p>
            
            <p>
              Records may be reviewed and deleted manually when they are no longer needed for these purposes. Retention periods may vary according to the nature of the record, the risk being investigated, and the systems used by our service providers.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Your Choices
            </h2>
            <p>
              You may opt out of SMS/MMS text messages at any time by replying <strong>STOP</strong> to any text message from Orlando Rivera for New York State Senate. You may reply <strong>HELP</strong> for help.
            </p>
            
            <p>
              You may also contact us at <a href="mailto:info@orlandojrivera.com" data-testid="privacy-email-link-2" className="text-campaignRed hover:underline">info@orlandojrivera.com</a> with questions or requests regarding your information.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Your Rights
            </h2>
            <p>
              Depending on where you are located, applicable law may provide certain rights regarding your personal information. These rights may include requesting access to, correction of, deletion of, or information about the personal information we maintain about you.
            </p>
            
            <p>
              To exercise privacy-related rights or make a request, contact us at <a href="mailto:info@orlandojrivera.com" data-testid="privacy-email-link-3" className="text-campaignRed hover:underline">info@orlandojrivera.com</a>. We will review and respond to requests in accordance with applicable law.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Third-Party Links
            </h2>
            <p>
              Our website, text messages, emails, or other campaign communications may include links to third-party websites, donation platforms, event pages, social media platforms, voter information resources, or other websites.
            </p>
            
            <p>
              Orlando Rivera for New York State Senate is not responsible for the privacy practices, content, or policies of third-party websites.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Changes and clarifications take effect when posted.
            </p>
            
            <h2 className="font-oswald text-2xl font-bold uppercase mt-8 mb-4 text-campaignBlack">
              Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy or would like to access, correct, amend, or delete personal information you have provided, contact Orlando Rivera for New York State Senate at: <a href="mailto:info@orlandojrivera.com" data-testid="privacy-email-link-4" className="text-campaignRed hover:underline">info@orlandojrivera.com</a>
            </p>
            
            <p className="text-sm text-gray-600 mt-12 pt-8 border-t border-gray-200">
              Paid for by Orlando for Senate.
            </p>
          </div>
        </div>
      </div>
    </div>
    </LegalLayout>
  );
}

